import type { ReadCallback } from 'i18next';
import LocalStorageBackend from 'i18next-localstorage-backend';

/**
 * `i18next-localstorage-backend` resolves its `read` callback synchronously.
 *
 * react-i18next loads missing namespaces from inside the render phase (the suspense
 * `new Promise(...)` executor in `useTranslation`), so a synchronous cache hit ends up calling
 * `resourceStore.addResourceBundle` — and emitting the `added` event — while React is still
 * rendering. Every mounted `useTranslation` then re-renders mid-render, which React reports as
 * "Cannot update a component while rendering a different component".
 *
 * Deferring the callback keeps the cache behaviour identical while guaranteeing the store update
 * (and its `added` event) happens outside the render phase, like every other i18next backend.
 */
class AsyncLocalStorageBackend extends LocalStorageBackend {
  static override type = 'backend' as const;

  override read(language: string, namespace: string, callback: ReadCallback) {
    // Extra arguments (e.g. the cache timestamp used by `cacheHitMode`) are forwarded as-is.
    super.read(language, namespace, (...args: Parameters<ReadCallback>) => {
      queueMicrotask(() => callback(...args));
    });
  }
}

export { AsyncLocalStorageBackend };
